export enum CoveredMatrixExecutionMethod {
  BothSides = 1,
  BaseAlreadyOwned = 2,
  FutureBasePurchase = 3,
}

export enum CoveredMatrixVolumeCalculationType {
  FixedAmount = 1,
  AssetPercent = 3,
  CoveredCallImbalancePercent = 6,
}

export enum CoveredMatrixState {
  Draft = 1,
  Paused = 2,
  Completed = 3,
  Deleted = 4,
}

export interface CoveredMatrixItemInput {
  optionIsin: string;
  criterionType: number;
  criterionValue: number | null;
}

export interface CreateCoveredMatrixOrderRequest {
  accountIds: number[];
  baseIsin: string;
  executionMethod: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice: number | null;
  volumeCalculationType: CoveredMatrixVolumeCalculationType;
  volumeInput: number;
  sendOrderType: number;
  items: CoveredMatrixItemInput[];
}

export interface CoveredMatrixOrderPreviewItem extends CoveredMatrixItemInput {
  optionName: string;
  optionEquivalentVolume: number;
  willCreateOrder: boolean;
}

export interface CoveredMatrixOrderPreviewRow {
  accountId: number;
  accountName: string;
  baseIsin: string;
  baseSymbolName: string;
  baseHoldingVolume: number;
  portfolioCoveredCallImbalance: number;
  targetVolume: number;
  executionMethod: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice?: number | null;
  volumeCalculationType: CoveredMatrixVolumeCalculationType;
  volumeInput: number;
  sendOrderType: number;
  items: CoveredMatrixOrderPreviewItem[];
}

export interface CoveredMatrixOrderPreviewResult {
  rows: CoveredMatrixOrderPreviewRow[];
  errors: string[];
  warnings: string[];
}

export enum CoveredMatrixCriterionType {
  MinDailyReturn = 1,
  MaxCost = 3,
  FixedCallPrice = 7,
}

export interface CoveredMatrixItem extends CoveredMatrixItemInput {
  id: number;
  coveredCallOrderId: number | null;
  isActive?: boolean;
  optionName?: string;
  totalVolume?: number;
  doneVolume?: number;
}

export interface CoveredMatrixCallOption {
  id: string;
  name: string;
  dueDate: string;
  strikePrice: number;
}

export interface CoveredMatrixMarketWatch {
  tseLink: string;
  lastPrice: number;
  tradePrice: number;
  buyVolume: number | null;
  buyPrice: number | null;
  sellPrice: number | null;
  sellVolume: number | null;
  buyOrderBookIsValid: boolean;
  sellOrderBookIsValid: boolean;
}

export interface CoveredMatrixPortfolioImbalance {
  groupId: number;
  sahraUserId: number;
  baseIsin: string;
  imbalance: number;
}

export interface CoveredMatrixLiveCriterionRequest {
  baseIsin: string;
  executionMethod: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice: number | null;
  items: Array<{
    optionIsin: string;
    criterionType: number;
  }>;
}

export interface CoveredMatrixLiveCriterion {
  optionIsin: string;
  value: number | null;
  gainAfterCommissionDaily: number | null;
  livePairPosition: number | null;
}

export interface CoveredMatrixGroup {
  id: number;
  batchId: string;
  sahraUserId: number;
  accountName?: string;
  baseIsin: string;
  executionMethod: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice: number | null;
  volumeCalculationType?: CoveredMatrixVolumeCalculationType;
  volumeInput?: number;
  targetVolume: number;
  sentVolume: number;
  baseHoldingVolume?: number;
  imbalance?: number;
  sendOrderType?: number;
  isActive?: boolean;
  state: CoveredMatrixState;
  baseSymbolName?: string;
  items: CoveredMatrixItem[];
  applyToSimilar?: boolean;
  removedItems?: CoveredMatrixItem[];
}

export interface CoveredMatrixOrderTableSearchParams {
  sahraUserId?: number;
  accountId?: number;
  instrumentId?: string;
  profileId?: number;
}

export interface CoveredMatrixOrderTableReport {
  id: number;
  coveredCallOrderIds: number[];
  row: number;
  sahraUserId: number;
  baseIsin: string;
  optionIsins: string[];
  optionSymbolNames: string[];
  criterionTypes: number[];
  criterionValues: Array<number | null>;
  optionSymbols: import("./TableInterfaces").TableHoverTableValue;
  targetCriterion: import("./TableInterfaces").TableHoverTableValue;
  executionMethod: CoveredMatrixExecutionMethod;
  manualBaseReferencePrice: number | null;
  volumeCalculationType: CoveredMatrixVolumeCalculationType;
  volumeInput: number;
  sendOrderTypeValue: number;
  isActive: boolean;
  state: CoveredMatrixState;
  accountName: string;
  baseSymbolName: string;
  executionMethodName: string;
  targetVolume: number;
  baseHoldingVolume: number;
  sentVolume: number;
  imbalance: number;
  doneSummary: import("./TableInterfaces").TableHoverTableValue;
  openVolumeSummary: import("./TableInterfaces").TableHoverTableValue;
  imbalanceSummary: import("./TableInterfaces").TableHoverTableValue;
  stateName: string;
  optionCount: number;
  creationDate: string;
}

export interface CoveredMatrixOrderItemTableSearchParams {
  groupId: number;
}

export interface CoveredMatrixExecutionTableSearchParams {
  accountId?: number;
  instrumentId?: string;
  profileId?: number;
}

export interface CoveredMatrixTradeTableReport extends CoveredMatrixOrderTableReport {
  tradeOffset: string;
  priceBeforeCommission: import("./TableInterfaces").TableHoverTableValue;
  priceAfterCommission: import("./TableInterfaces").TableHoverTableValue;
  dailyReturn: import("./TableInterfaces").TableHoverTableValue;
  annualReturn: import("./TableInterfaces").TableHoverTableValue;
  completionPercent: import("./TableInterfaces").TableHoverTableValue;
  criterionName: string;
}

export interface CoveredMatrixErrorTableReport {
  id: number;
  row: number;
  account: string;
  process: string;
  symbolName: string;
  side: string;
  price: number;
  volume: number;
  message: string | null;
  code: string | null;
  date: string;
}

export interface CoveredMatrixOrderItemTableReport {
  id: number;
  row: number;
  key: string;
  optionIsin: string;
  optionSymbolName: string;
  optionTseLink: string;
  optionLink: import("./TableInterfaces").TableIconLinkValue;
  buyVolume: number | null;
  buyPrice: number | null;
  sellPrice: number | null;
  sellVolume: number | null;
  gainAfterCommissionDaily: number | null;
  livePairPosition: number | null;
  openVolume: string;
  totalVolume: number;
  executedVolume: number;
  optionContractSize: number;
  doneBaseEquivalentVolume: number;
  baseSentVolume: number;
  optionSentVolume: number;
  baseOpenVolume: number;
  optionOpenVolume: number;
  optionEquivalentOpenVolume: number;
  baseDoneVolume: number;
  optionDoneVolume: number;
  optionEquivalentDoneVolume: number;
  baseImbalanceVolume: number;
  optionImbalanceVolume: number;
  optionEquivalentImbalanceVolume: number;
  creditAvailability: number | null;
  nonCreditAvailability: number | null;
  sentProgress: import("./TableInterfaces").TableProgressBarValue;
  executedProgress: import("./TableInterfaces").TableProgressBarValue;
  donePriceBeforeCommission: string | null;
  donePriceAfterCommission: string | null;
  targetCriterion: number | null;
  targetVolume: number;
  sentVolume: number;
  coveredCallOrderId: number | null;
  isActive: boolean;
}

export interface UpdateCoveredMatrixCriteriaRequest {
  groupId: number;
  targetVolume: number;
  applyToSimilar: boolean;
  items: (CoveredMatrixItemInput & { id: number; isDeleted?: boolean })[];
}
